/** Roster mapping for clicker and card ids (STORY-088): the pure half of the course screen's
 * section. The id rules mirror `packages/events/src/events/devices.py`; both read the same
 * examples file in their tests. ASCII on purpose: `[0-9]`, never `\d`, so a digit from another
 * script is refused on both sides. Returns null where the Python raises. */
import type { CourseDevice } from "../api/types";
import type { CourseDeviceIn, RosterEntry } from "./api";

export type MappableChannel = "clicker" | "card";

/** The highest number printed on a card (STORY-089: cards 1 to 63). */
export const CARD_MAX = 63;

const number = (raw: string): number | null => {
  const text = raw.replace(/^[ \t]+|[ \t]+$/g, "");
  return /^[0-9]{1,4}$/.test(text) ? Number(text) : null;
};

export function canonicalDeviceKey(channel: string, raw: string): string | null {
  if (channel === "clicker") {
    const key = raw.replace(/[:\- \t]/g, "").toLowerCase();
    return /^[0-9a-f]{12}$/.test(key) ? key : null;
  }
  if (channel === "card") {
    const n = number(raw);
    return n !== null && n >= 1 && n <= CARD_MAX ? String(n) : null;
  }
  return null;
}

export function canonicalSeat(raw: string): string | null {
  const n = number(raw);
  return n === null ? null : String(n);
}

/** Are clickers offered here (STORY-086, decision 5)? On a server, when the `clickers` flag is;
 * on a local host (the Teacher Computer app, which reads the hub) always: nobody can set that
 * host's flags. The same local-host test as `cardsEnabledFrom`. */
export const clickersEnabledFrom = (flags: Readonly<Record<string, boolean | undefined>>, localHost: boolean): boolean =>
  flags.clickers === true || localHost;

/** The channels the section offers, in this order: `clicker` when `clickersEnabledFrom` holds,
 * `card` when STORY-089's `useCardsEnabled()` holds (the `cards` flag, or any local host). The
 * section renders only when this is not empty, and lists only these channels' rows. */
export function deviceChannels({ clickers, cards }: { clickers: boolean; cards: boolean }): MappableChannel[] {
  return [...(clickers ? (["clicker"] as const) : []), ...(cards ? (["card"] as const) : [])];
}

/** The rows of the channels offered, and no other. */
export const rowsOffered = (rows: CourseDevice[], channels: readonly MappableChannel[]): CourseDevice[] =>
  rows.filter((r) => (channels as readonly string[]).includes(r.channel));

/** A card row that names a student: stored, but not used. A card joins by its seat or its
 * number, because a join cannot take a student until STORY-086's gateway `subject`. */
export const cardStudentRow = (row: Pick<CourseDevice, "channel" | "subject" | "seat">): boolean =>
  row.channel === "card" && row.seat == null && !!row.subject;

/** The roster's name for a subject, or null when it has none. */
export function studentName(subject: string | null | undefined, roster: RosterEntry[]): string | null {
  if (!subject) return null;
  return roster.find((r) => r.subject === subject && r.label)?.label ?? null;
}

const DEVICE_CHANNELS = new Set(["clicker", "card"]);

/** A clicker's or a card's own pseudonymous person: every channel it joined by is a device. */
const deviceOnly = (entry: RosterEntry) => entry.channels.length > 0 && entry.channels.every((c) => DEVICE_CHANNELS.has(c));

/** A label that is a seat number, as a `join_mode` seat lesson stores it (STORY-086, from
 * STORY-088's review). The complete fix, a roster field that marks a seat, is a later story. */
const seatLabel = (label: string) => /^[0-9]{1,4}$/.test(label);

/** Who the form offers as a student: a subject the teacher can recognise by name, and never a
 * person who only ever joined as a clicker or a card, nor one known only by a seat number. */
export function studentChoices(roster: RosterEntry[]): RosterEntry[] {
  return roster
    .filter((r) => r.subject && r.label && !seatLabel(r.label) && !deviceOnly(r))
    .sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));
}

/** The form's values, as `DeviceMapForm` holds them. */
export type DeviceFormValues = {
  channel: MappableChannel;
  key: string;
  slot: "seat" | "student";
  seat: string;
  subject: string;
  note: string;
  /** an existing row: its id is fixed, a new id is a new row */
  editing: boolean;
};

export type SlotContext = { guestOnly: boolean; roster: RosterEntry[] };

/** The student select's options: the named choices, plus the subject the values already hold
 * when the roster does not name it (a row being changed while the roster is loading, failed or
 * no longer names that person). `label` null reads as `course.devices.unnamed`. */
export function studentOptions(values: Pick<DeviceFormValues, "subject">, { roster }: Pick<SlotContext, "roster">): { subject: string; label: string | null }[] {
  const options = studentChoices(roster).map((r) => ({ subject: r.subject!, label: r.label }));
  if (values.subject && !options.some((o) => o.subject === values.subject)) options.push({ subject: values.subject, label: null });
  return options;
}

/** The slot the form shows and Save sends: the one rule for both. A seat for a card (it
 * cannot join as a student yet), when the course is guest-only, or when there is no student to
 * offer; else the teacher's choice. */
export function effectiveSlot(values: Pick<DeviceFormValues, "slot" | "subject"> & { channel?: MappableChannel }, ctx: SlotContext): "seat" | "student" {
  return values.channel === "card" || ctx.guestOnly || studentOptions(values, ctx).length === 0 ? "seat" : values.slot;
}

/** The PUT body for the form's values, or the refusal code the form shows instead. */
export function deviceBody(values: DeviceFormValues, ctx: SlotContext): { key: string; body: CourseDeviceIn } | { refusal: string } {
  const key = canonicalDeviceKey(values.channel, values.key);
  if (!key) return { refusal: "bad_device_key" };
  let body: CourseDeviceIn;
  if (effectiveSlot(values, ctx) === "seat") {
    const seat = canonicalSeat(values.seat);
    if (seat === null) return { refusal: "bad_seat" };
    body = { seat };
  } else {
    if (!values.subject) return { refusal: "seat_or_student" };
    body = { subject: values.subject };
  }
  // a guest-only course keeps no note: changing a row saved before the switch drops it
  body.label = ctx.guestOnly ? "" : values.note.trim();
  return { key, body };
}

/** True when this person also joins some other way (a phone, a browser): a device mapped to
 * them would make the live count show them twice. The roster keeps no channel for joins in an
 * anonymous-mode session, so this cannot fire for a person who only joined those. */
export function joinsAnotherWay(entry: RosterEntry | null | undefined): boolean {
  return !!entry && entry.channels.some((c) => !DEVICE_CHANNELS.has(c));
}

/** Seats in numeric order, then students by roster name, then by key. */
export function sortDeviceRows(rows: CourseDevice[], roster: RosterEntry[]): CourseDevice[] {
  const rank = (d: CourseDevice) => (d.seat != null ? 0 : 1);
  return [...rows].sort((a, b) => {
    if (rank(a) !== rank(b)) return rank(a) - rank(b);
    if (a.seat != null && b.seat != null && a.seat !== b.seat) return Number(a.seat) - Number(b.seat);
    if (a.seat == null && b.seat == null) {
      const by = (studentName(a.subject, roster) ?? "￿").localeCompare(studentName(b.subject, roster) ?? "￿");
      if (by !== 0) return by;
    }
    return a.channel === b.channel ? a.device_key.localeCompare(b.device_key) : a.channel.localeCompare(b.channel);
  });
}

/** The refusal codes `routes_devices.py` raises, each with its sentence. */
export const REFUSAL_CODES = [
  "bad_channel",
  "bad_device_key",
  "seat_or_student",
  "bad_seat",
  "seat_only_course",
  "note_on_guest_only",
  "seat_taken",
  "student_taken",
] as const;

export function refusalKey(code: string | null | undefined): string {
  return (REFUSAL_CODES as readonly string[]).includes(code ?? "") ? `course.devices.refusal.${code}` : "course.devices.refusal.general";
}
